import { loadScript } from "@/utils/helpers";
import type {
  PayPalButtonRenderParams,
  PayPalEnv,
  PayPalTokenizeDetails,
  PayPalTokenizePayload,
} from "./paypal";

const BRAINTREE_VERSION = "3.39.0";
const BRAINTREE_BASE = `https://js.braintreegateway.com/web/${BRAINTREE_VERSION}/js`;
const PAYPAL_CHECKOUT_SCRIPT = "https://www.paypalobjects.com/api/checkout.js";

type Logger = Pick<Console, "error" | "warn">;

declare global {
  interface Window {
    braintree?: {
      client: {
        create: (
          config: { authorization: string },
          callback: (err: Error | null, clientInstance: unknown) => void
        ) => void;
      };
      paypalCheckout: {
        create: (
          config: { client: unknown },
          callback: (err: Error | null, instance: unknown) => void
        ) => void;
      };
      dataCollector?: {
        create: (
          config: { client: unknown; paypal?: boolean },
          callback: (err: Error | null, data: { deviceData: string }) => void
        ) => void;
      };
    };
    paypal?: {
      Button: {
        render: (
          config: {
            braintree: unknown;
            client: { [env: string]: string };
            env: PayPalEnv;
            style?: Record<string, unknown>;
            payment: (
              data: unknown,
              actions: {
                braintree: { create: (opts: unknown) => Promise<string> };
              }
            ) => Promise<string>;
            onAuthorize: (
              data: { paymentToken: string },
              actions: {
                payment: { tokenize: (data: unknown) => Promise<unknown> };
              }
            ) => Promise<void>;
            onCancel?: () => void;
            onError?: (err: Error) => void;
          },
          container: string | HTMLElement
        ) => Promise<void>;
      };
    };
  }
}

/**
 * Loads core Braintree scripts (client, paypal-checkout module, data-collector).
 * Idempotent — safe to call multiple times.
 */
const loadBraintreeCore = async (): Promise<void> => {
  if (window.braintree?.client && window.braintree?.paypalCheckout) return;
  await loadScript(`${BRAINTREE_BASE}/client.min.js`);
  await loadScript(`${BRAINTREE_BASE}/paypal-checkout.min.js`);
  await loadScript(`${BRAINTREE_BASE}/data-collector.min.js`);
};

/**
 * Loads PayPal Checkout.js v4. Called on-demand when rendering PayPal button.
 * Idempotent — safe to call multiple times.
 */
const loadPayPalCheckout = async (): Promise<void> => {
  if (window.paypal?.Button) return;
  await loadScript(PAYPAL_CHECKOUT_SCRIPT);
};

/**
 * Loads all Braintree and PayPal Checkout scripts. Idempotent.
 * @deprecated Prefer lazy-loading via initBraintree + renderPayPalButton for better performance.
 */
export const loadBraintreeScripts = async (): Promise<void> => {
  await loadBraintreeCore();
  await loadPayPalCheckout();
};

/**
 * Initializes Braintree: loads core scripts (client, paypal-checkout module, data-collector).
 * PayPal Checkout.js is lazy-loaded on-demand when rendering the button.
 * Called from App.tsx during initialization.
 */
export const initBraintree = async (): Promise<void> => {
  await loadBraintreeCore();
};

/**
 * Returns the Braintree SDK from window (callback-based client for v3.39.0).
 * @throws Error if Braintree SDK is not available.
 */
export const getBraintreeInstance = (): NonNullable<
  typeof window.braintree
> => {
  if (!window.braintree?.client || !window.braintree?.paypalCheckout) {
    throw new Error("Braintree SDK not available");
  }
  return window.braintree;
};

/** Creates a Braintree client instance from the authorization token. */
const createBraintreeClient = (clientToken: string): Promise<unknown> =>
  new Promise((resolve, reject) => {
    const braintree = getBraintreeInstance();
    braintree.client.create(
      { authorization: clientToken },
      (err, clientInstance) => {
        if (err) return reject(err);
        resolve(clientInstance);
      }
    );
  });

/**
 * Collects Braintree device data for fraud prevention using an existing client instance.
 * Resolves to an empty string if dataCollector is not available on the SDK.
 */
const collectDeviceDataFromClient = (
  clientInstance: unknown
): Promise<string> =>
  new Promise<string>((resolve, reject) => {
    const braintree = getBraintreeInstance();
    if (!braintree.dataCollector) return resolve("");
    braintree.dataCollector.create(
      { client: clientInstance, paypal: true },
      (error, data) => {
        if (error) return reject(error);
        resolve(data.deviceData || "");
      }
    );
  });

/**
 * Collects device data for fraud prevention. Used for returning customers paying
 * with a vaulted SPA — only device data is needed.
 */
export const collectBraintreeDeviceData = async (
  clientToken: string
): Promise<string> => {
  const clientInstance = await createBraintreeClient(clientToken);
  return collectDeviceDataFromClient(clientInstance);
};

/**
 * Resolves the PayPal env. Trusts the BE-provided value when valid;
 * otherwise falls back to the build-time env so we never accidentally
 * point production at the sandbox endpoint or vice versa.
 */
const resolvePayPalEnv = (
  env: PayPalEnv | undefined,
  logger: Logger
): PayPalEnv => {
  if (env === "sandbox" || env === "production") return env;
  const fallback: PayPalEnv = import.meta.env.PROD ? "production" : "sandbox";
  if (env !== undefined) {
    logger.warn(
      `[PayPal] Invalid env "${env}" received from BE, falling back to "${fallback}"`
    );
  }
  return fallback;
};

/**
 * Renders the PayPal button using Braintree SDK v3.39.0 + PayPal Checkout.js v4.
 * Uses the vault flow and collects device data for fraud prevention.
 * @param params - Button render params (clientToken, env, amount, currencyCode, container, callbacks).
 * @returns Cleanup function to remove the button, or undefined if render failed.
 */
export const renderPayPalButton = async (
  params: PayPalButtonRenderParams
): Promise<(() => void) | undefined> => {
  const {
    clientToken,
    env,
    amount,
    currencyCode,
    container,
    onApprove,
    onCancel,
    onError,
    signal,
    logger = console,
  } = params;

  try {
    await loadPayPalCheckout();

    if (signal?.aborted) return undefined;

    const braintree = getBraintreeInstance();

    if (!window.paypal?.Button) {
      onError?.(new Error("PayPal SDK not available"));
      return undefined;
    }

    const paypalEnv = resolvePayPalEnv(env, logger);

    // Create a single client instance upfront so device data collection in
    // onAuthorize reuses it instead of creating a redundant connection.
    const clientInstance = await createBraintreeClient(clientToken);

    if (signal?.aborted) return undefined;

    await window.paypal.Button.render(
      {
        braintree,
        client: { [paypalEnv]: clientToken },
        env: paypalEnv,
        style: {
          height: 55,
          label: "pay",
          tagline: false,
          size: "responsive",
          color: "blue",
          shape: "rect",
        },
        payment: (
          _: unknown,
          actions: { braintree: { create: (opts: unknown) => Promise<string> } }
        ) =>
          actions.braintree.create({
            flow: "vault",
            currency: currencyCode,
            amount: Number(amount.toFixed(2)),
          }),
        onAuthorize: async (
          data: { paymentToken: string },
          actions: { payment: { tokenize: (d: unknown) => Promise<unknown> } }
        ) => {
          if (signal?.aborted) return;
          try {
            const deviceData =
              await collectDeviceDataFromClient(clientInstance);
            const tokenized = (await actions.payment.tokenize(data)) as {
              nonce: string;
              details: PayPalTokenizeDetails;
            };

            if (signal?.aborted) return;

            const { details } = tokenized;
            const result: PayPalTokenizePayload = {
              nonce: tokenized.nonce,
              type: "PayPalAccount",
              deviceData,
              details: {
                email: details.email,
                payerId: details.payerId,
                firstName: details.firstName,
                lastName: details.lastName,
                countryCode: details.countryCode,
                phone: details.phone,
                billingAddress: details.billingAddress
                  ? {
                      line1: details.billingAddress.line1,
                      city: details.billingAddress.city,
                      state: details.billingAddress.state,
                      postalCode: details.billingAddress.postalCode,
                      countryCode: details.billingAddress.countryCode,
                    }
                  : undefined,
              },
            };
            onApprove(result);
          } catch (err) {
            logger.error("[PayPal] tokenize error:", err);
            onError?.(err instanceof Error ? err : new Error(String(err)));
          }
        },
        onCancel: () => {
          onCancel?.();
        },
        onError: (err: Error) => {
          logger.error("[PayPal] button error:", err);
          onError?.(err);
        },
      },
      container
    );

    return () => {
      container.innerHTML = "";
    };
  } catch (err) {
    logger.error("[PayPal] initialization error:", err);
    onError?.(err instanceof Error ? err : new Error(String(err)));
    return undefined;
  }
};
